/*
 * In React Router v7, changing the URL hash (e.g., adding or removing "#/hotel-reviews") can trigger browser-native
 * anchor scrolling, which scrolls the page back to the top when opening or closing a modal driven by hash navigation.
 *
 * This hook preserves the user's scroll position across hash changes. When a hash is added, it snapshots the current scroll position.
 * When the hash is removed, it restores that scroll position after layout stabilization,
 * preventing unwanted scroll jumps during modal open/close transitions.
 */
import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

export function useHashScrollRestoration() {
  const { hash } = useLocation();
  const prevHashRef = useRef<string>('');
  const lastKnownY = useRef<number>(0);
  const savedOnOpenY = useRef<number | null>(null);

  useEffect(() => {
    lastKnownY.current = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          lastKnownY.current = window.scrollY;
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const prevHash = !!prevHashRef.current;
    const currHas = !!hash;

    if (!prevHash && currHas) {
      savedOnOpenY.current = lastKnownY.current;
    }

    if (prevHash && !currHas && typeof savedOnOpenY.current === 'number') {
      const y = savedOnOpenY.current;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          window.scrollTo({ top: y, left: 0, behavior: 'auto' });
        }),
      );
      savedOnOpenY.current = null;
    }

    prevHashRef.current = hash;
  }, [hash]);
}
